import { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useAuthState } from "@/hooks/useAuth"
import { useUserData } from "@/hooks/useUserData"
import { FYP_ENDPOINT } from "@/config/endpoints"
import { db } from "@/lib/firebase"
import { doc, getDoc } from "firebase/firestore"
import type { PortfolioItem } from "@/types/models"
import { useFeedCache } from "@/contexts/FeedCacheContext"

export interface QueryTattoosByFypFilters {
  collections: ("flashes" | "portfolioItems" | "healedPortfolioItems")[]
  cities?: string[]
  artistId?: string
  style?: string
  includeBooked?: boolean
  deals?: boolean
}

interface TattooItemRef {
  type: "flash" | "portfolioItem" | "healedPortfolioItem"
  id: string
  score?: number
}

interface QueryTattoosByFypResponse {
  left: TattooItemRef[]
  right: TattooItemRef[]
}

const ITEMS_PER_LOAD = 40

/**
 * Hook to query tattoos from the backend using the FYP (For You Page) endpoint
 * Handles fetching document IDs from backend and loading actual documents in batches.
 * When a cacheKey is provided, results are stored in FeedCacheContext so navigating
 * back to the feed shows the previous results instantly.
 */
export function useQueryTattoosByFyp(filters: QueryTattoosByFypFilters | undefined, cacheKey?: string) {
  const { user } = useAuthState()
  const { isAdminModeActive } = useUserData()
  const feedCache = useFeedCache()

  const cachedEntry = cacheKey ? feedCache.getEntry(cacheKey) : null

  const [allLeftItems, setAllLeftItems] = useState<TattooItemRef[]>(() => cachedEntry?.allLeftItems ?? [])
  const [allRightItems, setAllRightItems] = useState<TattooItemRef[]>(() => cachedEntry?.allRightItems ?? [])
  const [loadedItems, setLoadedItems] = useState<PortfolioItem[]>(() => cachedEntry?.items ?? [])
  const [searchScores, setSearchScores] = useState<Map<string, number>>(() => cachedEntry?.searchScores ?? new Map())
  const [loading, setLoading] = useState(!cachedEntry)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [initialLoadComplete, setInitialLoadComplete] = useState(!!cachedEntry)
  const [currentLoadedCount, setCurrentLoadedCount] = useState(() => cachedEntry?.currentLoadedCount ?? 0)
  
  // Track the current request ID to ignore stale results
  const currentRequestIdRef = useState(() => ({ current: 0 }))[0]
  // Track which item IDs we've already loaded to prevent duplicates
  const loadedItemIdsRef = useRef(new Set<string>(cachedEntry?.loadedItemIds ?? []))
  // Track the last cache key we hydrated from so we can reset when it changes
  const lastCacheKeyRef = useRef<string | undefined>(cacheKey)

  // Memoize filters to prevent unnecessary re-fetches
  const memoizedFilters = useMemo(() => {
    return JSON.stringify(filters)
  }, [filters])

  // Fetch IDs from backend when filters change
  useEffect(() => {
    const cacheKeyChanged = lastCacheKeyRef.current !== cacheKey
    lastCacheKeyRef.current = cacheKey

    if (!filters) {
      // Keep loading state while waiting for filters
      // But don't reset if we have a valid cache entry — show it while waiting
      const freshCacheEntry = cacheKey ? feedCache.getEntry(cacheKey) : null
      if (!freshCacheEntry) {
        setLoading(true)
        setInitialLoadComplete(false)
        setLoadedItems([])
        setAllLeftItems([])
        setAllRightItems([])
        loadedItemIdsRef.current.clear()
      }
      return
    }

    // If we have a fresh cache entry for this exact key (and key didn't just change), skip the network fetch
    const freshCacheEntry = cacheKey ? feedCache.getEntry(cacheKey) : null
    if (freshCacheEntry && !cacheKeyChanged) {
      setLoadedItems(freshCacheEntry.items)
      setAllLeftItems(freshCacheEntry.allLeftItems)
      setAllRightItems(freshCacheEntry.allRightItems)
      setCurrentLoadedCount(freshCacheEntry.currentLoadedCount)
      setSearchScores(freshCacheEntry.searchScores ?? new Map())
      loadedItemIdsRef.current = new Set(freshCacheEntry.loadedItemIds)
      setLoading(false)
      setInitialLoadComplete(true)
      return
    }

    // Safety check: FYP requires a valid user ID
    // If no user ID, return empty completed state (this should trigger Grid to not use FYP mode)
    if (!user?.uid) {
      console.warn("[useQueryTattoosByFyp] No user ID available, skipping FYP request and returning empty state")
      setLoading(false)
      setInitialLoadComplete(true)
      setLoadedItems([])
      setAllLeftItems([])
      setAllRightItems([])
      setSearchScores(new Map())
      setCurrentLoadedCount(0)
      setError(null)
      loadedItemIdsRef.current.clear()
      return
    }

    const fetchIds = async () => {
      // Increment request ID for this new query
      currentRequestIdRef.current += 1
      const thisRequestId = currentRequestIdRef.current
      
      try {
        setLoading(true)
        setError(null)
        setLoadedItems([])
        setSearchScores(new Map())
        setCurrentLoadedCount(0)
        setInitialLoadComplete(false)
        loadedItemIdsRef.current.clear()

        const requestBody = {
          userId: user.uid,
          collections: filters.collections,
          cities: filters.cities,
          artistId: filters.artistId,
          style: filters.style,
          includeBooked: filters.includeBooked ?? false,
          deals: filters.deals ?? false,
          interleave: true, // Default to true as per API spec
          isAdmin: isAdminModeActive,
        }

        console.log("[useQueryTattoosByFyp] Fetching IDs from FYP endpoint with filters:", filters, "requestId:", thisRequestId)
        console.log("[useQueryTattoosByFyp] Full request body:", requestBody)
        console.log("[useQueryTattoosByFyp] isAdminModeActive:", isAdminModeActive, "userId:", user.uid)

        const response = await fetch(FYP_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        })

        if (!response.ok) {
          throw new Error(`Backend returned ${response.status}: ${response.statusText}`)
        }

        const data: QueryTattoosByFypResponse = await response.json()
        
        // Check if this request is still current
        if (thisRequestId !== currentRequestIdRef.current) {
          console.log("[useQueryTattoosByFyp] Ignoring stale response for requestId:", thisRequestId, "current:", currentRequestIdRef.current)
          return
        }
        
        console.log("[useQueryTattoosByFyp] Received items from backend:", {
          leftCount: data.left.length,
          rightCount: data.right.length,
          totalCount: data.left.length + data.right.length,
          requestId: thisRequestId,
        })
        console.log("[useQueryTattoosByFyp] Item types from backend - left:", data.left.map(i => i.type), "right:", data.right.map(i => i.type))

        setAllLeftItems(data.left)
        setAllRightItems(data.right)

        // Build a map of item IDs to their scores
        const scoresMap = new Map<string, number>()
        const allItems = [...data.left, ...data.right]
        allItems.forEach(item => {
          if (item.score !== undefined) {
            scoresMap.set(item.id, item.score)
          }
        })
        setSearchScores(scoresMap)
        console.log("[useQueryTattoosByFyp] Scores map created:", {
          totalItems: allItems.length,
          itemsWithScores: scoresMap.size,
        })

        // Load first batch of items
        await loadItemsBatch(data.left, data.right, 0, ITEMS_PER_LOAD, thisRequestId)
        
        // Check again before setting complete (in case filters changed during batch load)
        if (thisRequestId === currentRequestIdRef.current) {
          setInitialLoadComplete(true)
        }
      } catch (err) {
        console.error("[useQueryTattoosByFyp] Error fetching IDs:", err)
        // Only set error if this is still the current request
        if (thisRequestId === currentRequestIdRef.current) {
          setError(err)
        }
      } finally {
        // Only clear loading if this is still the current request
        if (thisRequestId === currentRequestIdRef.current) {
          setLoading(false)
        }
      }
    }

    fetchIds()
  }, [memoizedFilters, user, isAdminModeActive, cacheKey])

  // Load a batch of items from Firestore
  const loadItemsBatch = async (
    leftItems: TattooItemRef[],
    rightItems: TattooItemRef[],
    startIndex: number,
    count: number,
    requestId: number
  ) => {
    // Calculate how many items to load from each column
    const itemsPerColumn = Math.ceil(count / 2)
    const leftBatch = leftItems.slice(startIndex, startIndex + itemsPerColumn)
    const rightBatch = rightItems.slice(startIndex, startIndex + itemsPerColumn)
    const allItemsToFetch = [...leftBatch, ...rightBatch]

    console.log("[useQueryTattoosByFyp] Loading batch:", {
      startIndex,
      count,
      leftBatchSize: leftBatch.length,
      rightBatchSize: rightBatch.length,
      totalToFetch: allItemsToFetch.length,
      requestId,
    })

    if (allItemsToFetch.length === 0) return

    // Fetch all documents in parallel - now we know exactly which collection to use
    const itemPromises = allItemsToFetch.map(async (itemRef) => {
      try {
        // Map the type to the correct collection name
        let collectionName = 
          itemRef.type === "flash" ? "flashes" : 
          itemRef.type === "healedPortfolioItem" ? "healedPortfolioItems" : 
          "portfolioItems"
        let docRef = doc(db, collectionName, itemRef.id)
        let docSnap = await getDoc(docRef)
        
        // For flashes, also check archived if not found
        if (!docSnap.exists() && itemRef.type === "flash") {
          collectionName = "flashesArchived"
          docRef = doc(db, collectionName, itemRef.id)
          docSnap = await getDoc(docRef)
        }
        
        // For healed items, mark them as healed in the portfolio item
        if (itemRef.type === "healedPortfolioItem" && docSnap.exists()) {
          console.log("[useQueryTattoosByFyp] Found healed portfolio item:", itemRef.id)
        }
        
        if (!docSnap.exists()) {
          console.warn(`[useQueryTattoosByFyp] Document ${itemRef.id} not found in ${collectionName}`)
          return null
        }

        const data = docSnap.data()

        if (itemRef.type === "flash" && data.status === "artistDeleted") {
          return null
        }

        const item: PortfolioItem = {
          id: docSnap.id,
          artistId: data.artistId as string,
          type: (data.type as PortfolioItem["type"]) ?? "image",
          storagePath: (data.storagePath as string) ?? "",
          imageUrl: data.imageUrl as string,
          width: (data.width as number) ?? 0,
          height: (data.height as number) ?? 0,
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
        }
        
        // Add optional properties
        if (data.compressedImageUrl) item.compressedImageUrl = data.compressedImageUrl
        if (data.styles) item.styleTags = data.styles as string[]
        if (data.caption) item.caption = data.caption as string
        if (data.timedSaves) {
          item.timedSaves = data.timedSaves.map((saveData: any) => ({
            itemId: saveData.itemId,
            userId: saveData.userId,
            savedAt: saveData.savedAt?.toDate?.() || new Date(saveData.savedAt)
          }))
        }

        // Flash-specific properties
        if (itemRef.type === "flash") {
          item.isFlash = true
          if (data.minPrice) item.minPrice = data.minPrice
          if (data.minSize) item.minSize = data.minSize
          if (data.available !== undefined) item.available = data.available
          if (data.booked !== undefined) item.booked = data.booked
          if (data.accessCode) item.accessCode = data.accessCode
          if (data.eventId) item.eventId = data.eventId
          if (data.eventExclusive !== undefined) item.eventExclusive = data.eventExclusive
          
          // Handle discount field
          if (data.discount) {
            item.discount = {
              absoluteAmount: data.discount.absoluteAmount,
              procentualAccount: data.discount.procentualAccount,
              newPrice: data.discount.newPrice,
              oldPrice: data.discount.oldPrice,
              discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
              discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
            }
          }
        }
        
        // Healed portfolio-specific properties
        if (itemRef.type === "healedPortfolioItem") {
          item.isHealed = true
        }
        
        return item
      } catch (err) {
        console.error(`[useQueryTattoosByFyp] Error fetching document ${itemRef.id}:`, err)
        return null
      }
    })

    const fetchedItems = await Promise.all(itemPromises)
    const validItems = fetchedItems.filter((item): item is PortfolioItem => item !== null)

    // Check if this request is still current before updating state
    if (requestId !== currentRequestIdRef.current) {
      console.log("[useQueryTattoosByFyp] Ignoring stale batch results for requestId:", requestId, "current:", currentRequestIdRef.current)
      return
    }

    const missingCount = allItemsToFetch.length - validItems.length
    console.log("[useQueryTattoosByFyp] Batch loaded:", {
      requested: allItemsToFetch.length,
      found: validItems.length,
      missing: missingCount,
      requestId,
    })

    if (missingCount > 0) {
      console.warn(`[useQueryTattoosByFyp] ${missingCount} documents failed to load or were missing`)
    }

    // Create a map to preserve the order from backend (left/right columns)
    const itemMap = new Map<string, PortfolioItem>()
    validItems.forEach(item => itemMap.set(item.id, item))

    // Reconstruct items in the correct order (interleaving left and right)
    // ALSO deduplicate against already-loaded items
    const orderedItems: PortfolioItem[] = []
    const maxLength = Math.max(leftBatch.length, rightBatch.length)
    
    for (let i = 0; i < maxLength; i++) {
      if (i < leftBatch.length && itemMap.has(leftBatch[i].id)) {
        const itemId = leftBatch[i].id
        if (!loadedItemIdsRef.current.has(itemId)) {
          orderedItems.push(itemMap.get(itemId)!)
          loadedItemIdsRef.current.add(itemId)
        }
      }
      if (i < rightBatch.length && itemMap.has(rightBatch[i].id)) {
        const itemId = rightBatch[i].id
        if (!loadedItemIdsRef.current.has(itemId)) {
          orderedItems.push(itemMap.get(itemId)!)
          loadedItemIdsRef.current.add(itemId)
        }
      }
    }

    const deduplicatedCount = validItems.length - orderedItems.length
    if (deduplicatedCount > 0) {
      console.warn(`[useQueryTattoosByFyp] Deduplicated ${deduplicatedCount} items that were already loaded`)
    }

    setLoadedItems(prev => {
      const updatedItems = [...prev, ...orderedItems]
      if (cacheKey) {
        feedCache.setEntry(cacheKey, {
          items: updatedItems,
          allLeftItems: leftItems,
          allRightItems: rightItems,
          loadedItemIds: new Set(loadedItemIdsRef.current),
          currentLoadedCount: loadedItemIdsRef.current.size,
          searchScores: undefined,
          cachedAt: Date.now(),
          scrollPosition: 0,
        })
      }
      return updatedItems
    })
    setCurrentLoadedCount(prev => prev + orderedItems.length)
  }

  // Load more items (for infinite scroll)
  const loadMoreItems = useCallback(async () => {
    if (loadingMore) return

    const totalAvailableItems = allLeftItems.length + allRightItems.length
    const hasMore = currentLoadedCount < totalAvailableItems

    if (!hasMore) {
      console.log("[useQueryTattoosByFyp] No more items to load")
      return
    }

    // Use the current request ID from ref (not the stale activeRequestId from state)
    const currentRequestId = currentRequestIdRef.current
    
    // Only proceed if we have a valid request ID
    if (currentRequestId === 0) {
      console.warn("[useQueryTattoosByFyp] No active request ID, skipping load more")
      return
    }

    console.log("[useQueryTattoosByFyp] Loading more items...", {
      currentLoaded: currentLoadedCount,
      totalAvailable: totalAvailableItems,
      requestId: currentRequestId,
    })

    setLoadingMore(true)

    try {
      // Calculate which items to load next from each column
      const itemsPerColumn = Math.ceil(currentLoadedCount / 2)
      await loadItemsBatch(allLeftItems, allRightItems, itemsPerColumn, ITEMS_PER_LOAD, currentRequestId)
    } catch (err) {
      console.error("[useQueryTattoosByFyp] Error loading more items:", err)
      setError(err)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, allLeftItems, allRightItems, currentLoadedCount, filters])

  const totalAvailableItems = allLeftItems.length + allRightItems.length
  const hasMore = currentLoadedCount < totalAvailableItems

  return {
    items: loadedItems,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMoreItems,
    initialLoadComplete,
    searchScores,
  }
}
